import { formatCount, tt } from '@agos/shared'

/**
 * Who works here, and what each of them can reach.
 *
 * **FB-099.** Legacy's role filter has four roles and the system has five. `roleFilterOptions` lists
 * *Všetky role*, `main_admin`, `admin`, `waiter`, `driver` — and no `cook`. There is one cook on the
 * staff, so that employee cannot be filtered for, and the counts on the chips do not add up: *Všetky*
 * says 23 while 2 + 6 + 3 + 11 is 22. One person is missing from every breakdown.
 *
 * Worse than a missing option: `cook` is the form's **default**.
 *
 * ```tsx
 * defaultValues: { name: "", position: "Obsluha", systemRole: "cook", … }
 * ```
 *
 * So an employee added without touching the role dropdown lands on the one role the filter cannot
 * select. The filter is built from the roles that exist here, which is one option per role by
 * construction, and the default is the role most of the staff actually hold.
 *
 * **FB-100.** `position` is free text with a default of `"Obsluha"`, and the data has drifted: 14
 * distinct positions across 23 people, including **`Šofer` six times and `Šofér` twice** — the same
 * job, two spellings — plus `veduci` and `Veduci`, and one employee whose position is the literal
 * string `waiter`, a system role typed into the wrong field. Nothing collapses them, so any report
 * grouped by position counts one job as two.
 */

export type SystemRole = 'main_admin' | 'admin' | 'cook' | 'waiter' | 'driver'

/** Every role the system has. Legacy's filter is missing the third one. */
export const SYSTEM_ROLES: readonly SystemRole[] = [
  'main_admin',
  'admin',
  'cook',
  'waiter',
  'driver',
]

// The thirteen fields this declared are a subset of the twenty-two the backend now publishes
// (BLOCKED #4), with the same nullability except `createdAt`, which this typed as nullable while the
// column is NOT NULL. Re-exported so the call sites in this module stay as they are and there is one
// definition rather than two that can drift.
import type { Employee } from '@agos/api-client/model'

export type { Employee }

/**
 * An employee being edited, which is not yet one the API answered.
 *
 * `Employee` gained a `required` list from its columns on 2026-08-03, so it means *a row that came
 * back* — eleven fields guaranteed. A form being filled in has none of them, and `employeeFormOf`
 * runs on exactly that: it is what turns a partial row into a complete form.
 */
export type EmployeeDraft = Partial<Employee>

const ROLE_LABELS: Record<SystemRole, string> = {
  main_admin: tt('Hlavný administrátor'),
  admin: tt('Administrátor'),
  cook: tt('Kuchár'),
  waiter: tt('Čašník'),
  driver: tt('Šofér'),
}

export type BadgeColor = 'zinc' | 'blue' | 'emerald' | 'red' | 'amber' | 'violet'

const ROLE_COLORS: Record<SystemRole, BadgeColor> = {
  main_admin: 'violet',
  admin: 'blue',
  cook: 'emerald',
  waiter: 'emerald',
  driver: 'amber',
}

/** What each role reaches — legacy's own legend, kept because it is the only place it is written. */
const ROLE_ACCESS: Record<SystemRole, string[]> = {
  main_admin: [tt('Celý systém'), tt('Správa zamestnancov'), tt('Nastavenia'), tt('Prehľady')],
  admin: [tt('Objednávky'), tt('Kuchyňa'), tt('Rozvoz'), tt('Produkty'), tt('Štatistiky')],
  cook: [tt('Kuchyňa'), tt('Obedové menu')],
  waiter: [tt('Nová objednávka (reštaurácia)'), tt('Kuchyňa'), tt('Peňaženka')],
  driver: [tt('Rozvozová plocha'), tt('Peňaženka')],
}

export function isSystemRole(value: string | undefined): value is SystemRole {
  return (SYSTEM_ROLES as readonly string[]).includes(value ?? '')
}

export function roleLabel(role: string | undefined): string {
  return isSystemRole(role) ? ROLE_LABELS[role] : (role ?? '')
}

export function roleColor(role: string | undefined): BadgeColor {
  return isSystemRole(role) ? ROLE_COLORS[role] : 'zinc'
}

export function roleAccess(role: SystemRole): string[] {
  return ROLE_ACCESS[role]
}

/** Initials for the avatar fallback — two at most, as legacy does. */
export function initialsOf(name: string | undefined): string {
  return (name ?? '')
    .split(/\s+/)
    .filter((word) => word !== '')
    .map((word) => word[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

// ─── the list ────────────────────────────────────────────────────────────────

export interface RosterFilter {
  tab: 'active' | 'inactive'
  role: string
  search: string
}

export function matchesSearch(employee: Employee, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (needle === '') return true
  return [employee.name, employee.email, employee.phone, employee.position].some((field) =>
    String(field ?? '').toLowerCase().includes(needle),
  )
}

export function filterRoster(employees: Employee[], filter: RosterFilter): Employee[] {
  return employees.filter((employee) => {
    const active = employee.active !== false
    if (filter.tab === 'active' ? !active : active) return false
    if (filter.role !== 'all' && employee.systemRole !== filter.role) return false
    return matchesSearch(employee, filter.search)
  })
}

export interface RoleOption {
  value: string
  label: string
  count: number
}

/**
 * One filter option per role that exists on the staff, plus *all* — **FB-099**.
 *
 * Built from the data rather than from a hand-written list, so the option cannot go missing and the
 * counts add up to the total by construction. A role nobody holds is not offered, because a filter
 * that always returns nothing is noise.
 */
export function roleOptions(employees: Employee[], tab: 'active' | 'inactive'): RoleOption[] {
  const pool = employees.filter((employee) =>
    tab === 'active' ? employee.active !== false : employee.active === false,
  )
  const counts = new Map<string, number>()
  for (const employee of pool) {
    const role = employee.systemRole ?? ''
    counts.set(role, (counts.get(role) ?? 0) + 1)
  }

  const options: RoleOption[] = [{ value: 'all', label: tt('Všetky role'), count: pool.length }]
  for (const role of SYSTEM_ROLES) {
    const count = counts.get(role) ?? 0
    if (count > 0) options.push({ value: role, label: ROLE_LABELS[role], count })
  }
  // A role the enum does not know about would otherwise vanish from the breakdown entirely.
  for (const [role, count] of counts) {
    if (!isSystemRole(role) && role !== '') {
      options.push({ value: role, label: role, count })
    }
  }
  return options
}

/** Whether the options account for everybody — the invariant legacy's hand-written list broke. */
export function optionsAddUp(options: RoleOption[]): boolean {
  const all = options.find((option) => option.value === 'all')?.count ?? 0
  const rest = options
    .filter((option) => option.value !== 'all')
    .reduce((total, option) => total + option.count, 0)
  return all === rest
}

export interface RosterTotals {
  total: number
  active: number
  inactive: number
  external: number
  withCard: number
}

export function rosterTotals(employees: Employee[]): RosterTotals {
  let active = 0
  let external = 0
  let withCard = 0
  for (const employee of employees) {
    if (employee.active !== false) active += 1
    if (employee.isExternal === true) external += 1
    if ((employee.hikvisionCardNo ?? '') !== '') withCard += 1
  }
  return {
    total: employees.length,
    active,
    inactive: employees.length - active,
    external,
    withCard,
  }
}

// ─── positions, and the drift in them — FB-100 ───────────────────────────────

/**
 * A position reduced to what it means: lower case, no diacritics, single-spaced.
 *
 * `Šofer` and `Šofér` are the same job. So are `veduci` and `Veduci`. Compared this way so the page
 * can say which of the fourteen positions in the data are duplicates of each other.
 */
export function normalisePosition(position: string | undefined): string {
  return (position ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .replace(/\s+/g, ' ')
}

export interface PositionGroup {
  /** The spelling most people share; ties go to the first alphabetically, for stability. */
  canonical: string
  spellings: string[]
  people: number
}

/** The positions in use, grouped by what they mean rather than by how they were typed. */
export function positionGroups(employees: Employee[]): PositionGroup[] {
  const groups = new Map<string, Map<string, number>>()
  for (const employee of employees) {
    const raw = (employee.position ?? '').trim()
    if (raw === '') continue
    const key = normalisePosition(raw)
    const spellings = groups.get(key) ?? new Map<string, number>()
    spellings.set(raw, (spellings.get(raw) ?? 0) + 1)
    groups.set(key, spellings)
  }

  return [...groups.values()]
    .map((spellings) => {
      const entries = [...spellings.entries()].sort(
        (left, right) => right[1] - left[1] || left[0].localeCompare(right[0]),
      )
      return {
        canonical: entries[0]?.[0] ?? '',
        spellings: entries.map(([spelling]) => spelling),
        people: entries.reduce((total, [, count]) => total + count, 0),
      }
    })
    .sort((left, right) => right.people - left.people || left.canonical.localeCompare(right.canonical))
}

/** Groups written more than one way — the ones a report grouped by position miscounts. */
export function driftedPositions(employees: Employee[]): PositionGroup[] {
  return positionGroups(employees).filter((group) => group.spellings.length > 1)
}

/** A position that is really a system role somebody typed into the wrong field. */
export function positionsThatAreRoles(employees: Employee[]): string[] {
  const found = new Set<string>()
  for (const employee of employees) {
    const raw = (employee.position ?? '').trim()
    if (raw !== '' && isSystemRole(raw.toLowerCase())) found.add(raw)
  }
  return [...found]
}

/** The spellings already in use, so the next one matches instead of adding a fifteenth. */
export function positionSuggestions(employees: Employee[]): string[] {
  return positionGroups(employees).map((group) => group.canonical)
}

/**
 * The spelling in use that this one means the same as, when they are written differently.
 *
 * Silent when the input *is* the canonical spelling, and silent when the position is new. It answers
 * on `Šofér` — already in use twice — because the commonest spelling is `Šofer`, and one job written
 * two ways is the drift FB-100 is about.
 */
export function duplicatesExistingPosition(
  position: string,
  employees: Employee[],
): string | null {
  const key = normalisePosition(position)
  if (key === '') return null
  const group = positionGroups(employees).find(
    (candidate) => normalisePosition(candidate.canonical) === key,
  )
  if (group === undefined) return null
  return group.canonical === position.trim() ? null : group.canonical
}

// ─── the form ────────────────────────────────────────────────────────────────

export interface EmployeeForm {
  name: string
  position: string
  systemRole: SystemRole
  phone: string
  email: string
  password: string
  active: boolean
  showWallet: boolean
  /**
   * A driver hired through an agency rather than on the payroll — §9.2.
   *
   * Only meaningful for a driver: it decides whether the per-delivery reward is computed at all, and
   * `/external-drivers` is the screen that pays them. Every other role ignores it.
   */
  isExternal: boolean
  hikvisionCardNo: string
}

/**
 * A new employee starts as whatever most of the staff are.
 *
 * Legacy hardcodes `cook`, which is the one role its own filter cannot select (FB-099) and the
 * rarest on the staff — one person out of 23.
 */
export function commonestRole(employees: Employee[]): SystemRole {
  const counts = new Map<SystemRole, number>()
  for (const employee of employees) {
    if (isSystemRole(employee.systemRole)) {
      counts.set(employee.systemRole, (counts.get(employee.systemRole) ?? 0) + 1)
    }
  }
  let best: SystemRole = 'waiter'
  let bestCount = -1
  for (const role of SYSTEM_ROLES) {
    // Never default somebody into an administrator.
    if (role === 'main_admin' || role === 'admin') continue
    const count = counts.get(role) ?? 0
    if (count > bestCount) {
      best = role
      bestCount = count
    }
  }
  return best
}

export function emptyEmployeeForm(employees: Employee[]): EmployeeForm {
  return {
    name: '',
    position: '',
    systemRole: commonestRole(employees),
    phone: '',
    email: '',
    password: '',
    active: true,
    showWallet: true,
    isExternal: false,
    hikvisionCardNo: '',
  }
}

export function employeeFormOf(employee: EmployeeDraft): EmployeeForm {
  return {
    name: employee.name ?? '',
    position: employee.position ?? '',
    systemRole: isSystemRole(employee.systemRole) ? employee.systemRole : 'waiter',
    phone: employee.phone ?? '',
    email: employee.email ?? '',
    password: '',
    active: employee.active !== false,
    showWallet: employee.showWallet !== false,
    isExternal: employee.isExternal === true,
    hikvisionCardNo: employee.hikvisionCardNo ?? '',
  }
}

export type EmployeeProblem =
  | { kind: 'no-name' }
  | { kind: 'name-too-short' }
  | { kind: 'no-position' }
  | { kind: 'position-is-role'; value: string }
  | { kind: 'position-duplicate'; existing: string }
  | { kind: 'bad-email'; value: string }
  | { kind: 'email-taken'; name: string }
  | { kind: 'password-too-short' }
  | { kind: 'no-credentials' }

/** Legacy's own minimum, kept. */
const MIN_PASSWORD = 6
const MIN_NAME = 2

/** Deliberately loose — the server is the authority, and a valid address can look odd. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export function employeeProblems(
  form: EmployeeForm,
  employees: Employee[],
  editingId: number | null,
): EmployeeProblem[] {
  const problems: EmployeeProblem[] = []
  const name = form.name.trim()
  if (name === '') problems.push({ kind: 'no-name' })
  else if (name.length < MIN_NAME) problems.push({ kind: 'name-too-short' })

  const position = form.position.trim()
  if (position === '') problems.push({ kind: 'no-position' })
  else if (isSystemRole(position.toLowerCase())) {
    // One employee in the data has `position = 'waiter'` (FB-100).
    problems.push({ kind: 'position-is-role', value: position })
  } else {
    const others = employees.filter((employee) => employee.id !== editingId)
    const duplicate = duplicatesExistingPosition(position, others)
    if (duplicate !== null) problems.push({ kind: 'position-duplicate', existing: duplicate })
  }

  const email = form.email.trim()
  if (email !== '') {
    if (!EMAIL_PATTERN.test(email)) problems.push({ kind: 'bad-email', value: email })
    else {
      const clash = employees.find(
        (employee) =>
          employee.id !== editingId &&
          (employee.email ?? '').trim().toLowerCase() === email.toLowerCase(),
      )
      if (clash !== undefined) problems.push({ kind: 'email-taken', name: clash.name ?? '' })
    }
  }

  const password = form.password.trim()
  if (password !== '' && password.length < MIN_PASSWORD) {
    problems.push({ kind: 'password-too-short' })
  }
  // An employee with no address and no password cannot sign in — legacy allows it silently.
  if (editingId === null && email === '' && password === '') {
    problems.push({ kind: 'no-credentials' })
  }

  return problems
}

/** The duplicate-position and no-credentials warnings do not block a save. */
export function canSaveEmployee(problems: EmployeeProblem[]): boolean {
  return !problems.some(
    (problem) =>
      problem.kind === 'no-name' ||
      problem.kind === 'name-too-short' ||
      problem.kind === 'no-position' ||
      problem.kind === 'position-is-role' ||
      problem.kind === 'bad-email' ||
      problem.kind === 'email-taken' ||
      problem.kind === 'password-too-short',
  )
}

/** An untouched password box sends nothing, so saving a name cannot clear a credential. */
export function employeePayload(form: EmployeeForm): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: form.name.trim(),
    position: form.position.trim(),
    systemRole: form.systemRole,
    phone: form.phone.trim() || null,
    email: form.email.trim() || null,
    active: form.active,
    showWallet: form.showWallet,
    // Sent only for a driver. On any other role the flag is meaningless, and writing `false` to every
    // cook and waiter would put a column nobody reads into every audit row of every save.
    ...(form.systemRole === 'driver' ? { isExternal: form.isExternal } : {}),
    hikvisionCardNo: form.hikvisionCardNo.trim() || null,
  }
  const password = form.password.trim()
  if (password !== '') payload.password = password
  return payload
}

// ─── what may be done to whom ────────────────────────────────────────────────

export type Restriction = 'own-account' | 'last-main-admin' | 'main-admin'

/**
 * Why an action is not offered.
 *
 * Legacy checks two of these and only after the click, as a toast: deactivating a `main_admin` and
 * deleting yourself. It does not check the third, which is the one that locks everybody out — the
 * *last* main administrator. There are two in this data, one of them a developer account.
 */
export function deactivateRestriction(
  employee: Employee,
  employees: Employee[],
): Restriction | null {
  if (employee.systemRole === 'main_admin') {
    const remaining = employees.filter(
      (candidate) =>
        candidate.systemRole === 'main_admin' &&
        candidate.active !== false &&
        candidate.id !== employee.id,
    ).length
    return remaining === 0 ? 'last-main-admin' : 'main-admin'
  }
  return null
}

export function deleteRestriction(
  employee: Employee,
  employees: Employee[],
  currentUserId: number | undefined,
): Restriction | null {
  if (employee.id !== undefined && employee.id === currentUserId) return 'own-account'
  if (employee.systemRole === 'main_admin') {
    const remaining = employees.filter(
      (candidate) => candidate.systemRole === 'main_admin' && candidate.id !== employee.id,
    ).length
    if (remaining === 0) return 'last-main-admin'
  }
  return null
}

export function restrictionText(restriction: Restriction): string {
  if (restriction === 'own-account') return tt('Vlastný účet zmazať nemôžete.')
  if (restriction === 'last-main-admin') {
    return tt('Toto je posledný hlavný administrátor - bez neho sa do systému nikto nedostane.')
  }
  return tt('Hlavného administrátora sa deaktivovať nedá.')
}

/**
 * Whether demoting this employee would leave nobody able to administer the system.
 *
 * The role dropdown lets a main administrator make themselves a driver. Nothing in legacy stops it,
 * and the next screen they see is the driver surface.
 */
export function demotionLocksEveryoneOut(
  form: EmployeeForm,
  employee: Employee,
  employees: Employee[],
): boolean {
  if (employee.systemRole !== 'main_admin' || form.systemRole === 'main_admin') return false
  return (
    employees.filter(
      (candidate) =>
        candidate.systemRole === 'main_admin' &&
        candidate.active !== false &&
        candidate.id !== employee.id,
    ).length === 0
  )
}

export { formatCount }
